import { useLayoutEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

/**
 * Detects whether a line-clamped element is actually truncated — i.e. its content
 * overflows the visible (clamped) box. CSS line-clamp exposes no truncation state, so
 * we compare scrollHeight vs clientHeight, re-measuring on resize (truncation depends
 * on width) and whenever `content` changes.
 *
 * Returns a ref to attach to the clamped element and the current truncation state.
 */
export default function useIsTruncated(content: string) {
    const ref = useRef<HTMLElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) {
            return undefined;
        }
        const measure = () => setIsTruncated(el.scrollHeight > el.clientHeight);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [content]);

    return { ref, isTruncated };
}
